import { useMemo, useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { useMoveCard, useTask } from '../hooks'
import type { Board, Task } from '../types'
import { ActionTypeFilter } from './ActionTypeFilter'
import { KanbanCard } from './KanbanCard'
import { KanbanColumn } from './KanbanColumn'
import { NewColumnDialog } from './NewColumnDialog'
import { TaskDetailDrawer } from './TaskDetailDrawer'

interface KanbanBoardProps {
  board: Board
  /** Pokazuje przycisk "+ Dodaj kolumnę". Default true. Embed na dashboardzie ukrywa go,
   *  bo zarządzanie strukturą boardu należy do /tasks (pełny widok). */
  showAddColumn?: boolean
}

/**
 * Composer dla kanbana - DndContext + kolumny + przycisk "Dodaj kolumnę".
 *
 * **Cross-column drag**: dnd-kit nie obcina drag wizualnie samodzielnie - to css `overflow-y-auto`
 * na ciele kolumny klipuje karty. Rozwiązanie: {@link DragOverlay} renderuje preview karty
 * w portalu na poziomie boardu, więc preview swobodnie pływa nad kolumnami.
 *
 * **Collision detection**: {@code pointerWithin} (zamiast {@code closestCenter}) - intersekcja
 * z drop zone'em pod kursorem; dla kanbana lepsze, bo działa naturalnie przy dropach na
 * (pustą) kolumnę lub na obszar zwykłego cardu.
 */
export function KanbanBoard({ board, showAddColumn = true }: KanbanBoardProps) {
  const moveCard = useMoveCard(board.id)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  )
  const [showNewColumn, setShowNewColumn] = useState(false)
  const [activeTask, setActiveTask] = useState<Task | null>(null)
  // Trzymamy tylko id taska, nie snapshot - dzieki temu drawer pobiera swieze dane
  // przez useTask query i reaguje na invalidacje (zmiana action_type, status, etc.)
  const [detailTaskId, setDetailTaskId] = useState<number | null>(null)
  const [actionTypeFilter, setActionTypeFilter] = useState<string | null>(null)
  const detailTaskQ = useTask(detailTaskId)

  const filteredColumns = useMemo(() => {
    if (!actionTypeFilter) return board.columns
    return board.columns.map((col) => ({
      ...col,
      cards: col.cards.filter((c) => c.task.actionTypeCode === actionTypeFilter),
    }))
  }, [board.columns, actionTypeFilter])

  const handleDragStart = (e: DragStartEvent) => {
    const activeId = String(e.active.id)
    if (!activeId.startsWith('card-')) return
    const taskId = Number(activeId.slice('card-'.length))
    const task = findTask(board, taskId)
    setActiveTask(task)
  }

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveTask(null)
    const { active, over } = e
    if (!over || active.id === over.id) return

    const activeId = String(active.id)
    if (!activeId.startsWith('card-')) return
    const taskId = Number(activeId.slice('card-'.length))

    const overId = String(over.id)
    const target = resolveTarget(board, taskId, overId)
    if (!target) return

    moveCard.mutate({ taskId, columnId: target.columnId, position: target.position })
  }

  return (
    <div className="flex flex-col h-full w-full">
      <div className="px-4 pt-2 pb-1 flex items-center gap-4 border-b border-slate-100">
        <ActionTypeFilter value={actionTypeFilter} onChange={setActionTypeFilter} />
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveTask(null)}
      >
        <div className="flex gap-3 p-4 overflow-x-auto h-full">
          {filteredColumns.map((col) => (
            <KanbanColumn
              key={col.id}
              column={col}
              board={board}
              onCardClick={(t) => setDetailTaskId(t.id)}
            />
          ))}
          {showAddColumn && (
            <button
              type="button"
              onClick={() => setShowNewColumn(true)}
              className="self-start mt-2 px-3 py-1.5 text-sm border border-dashed border-slate-300 rounded text-slate-600 hover:bg-slate-100 flex-shrink-0"
            >
              + Dodaj kolumnę
            </button>
          )}
        </div>
        <DragOverlay dropAnimation={{ duration: 150 }}>
          {activeTask ? <KanbanCard task={activeTask} /> : null}
        </DragOverlay>
      </DndContext>
      {showNewColumn && (
        <NewColumnDialog boardId={board.id} onClose={() => setShowNewColumn(false)} />
      )}
      <TaskDetailDrawer task={detailTaskQ.data ?? null} onClose={() => setDetailTaskId(null)} />
    </div>
  )
}

function findTask(board: Board, taskId: number): Task | null {
  for (const col of board.columns) {
    const card = col.cards.find((c) => c.task.id === taskId)
    if (card) return card.task
  }
  return null
}

/**
 * Zamienia event drop'u na konkretny (columnId, position) gdzie karta ma wylądować.
 *  - drop nad kartą innej kolumny → ta kolumna, pozycja tej karty (wstawienie przed)
 *  - drop nad pustą kolumną → pozycja 0
 *  - drop nad kartą w tej samej kolumnie → reorder do pozycji tej karty
 */
function resolveTarget(board: Board, _taskId: number, overId: string): { columnId: number; position: number } | null {
  if (overId.startsWith('column-')) {
    const columnId = Number(overId.slice('column-'.length))
    const col = board.columns.find((c) => c.id === columnId)
    if (!col) return null
    return { columnId, position: col.cards.length }
  }
  if (overId.startsWith('card-')) {
    const overTaskId = Number(overId.slice('card-'.length))
    for (const col of board.columns) {
      const idx = col.cards.findIndex((c) => c.task.id === overTaskId)
      if (idx >= 0) {
        return { columnId: col.id, position: idx }
      }
    }
  }
  return null
}

// Helper export dla testów (nie używany w runtime).
export const __test = { resolveTarget }
